import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { authAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { Loader2, Shield } from 'lucide-react';

const AdminLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      toast({
        title: "Error",
        description: "Please fill in all fields",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      const response = await authAPI.login(formData);

      if (!response?.success || !response?.user || !response?.token) {
        throw new Error("Invalid server response");
      }

      const { user, token } = response;

      if (user.role !== "super_admin") {
        toast({
          title: "Access Denied",
          description: "This login is for Super Admins only",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      localStorage.setItem("authToken", token);
      localStorage.setItem("userEmail", user.email);
      localStorage.setItem("userFullName", user.fullName || "");
      localStorage.setItem("userRole", user.role);
      localStorage.setItem("companyId", user.companyId || "");

      toast({
        title: "Success!",
        description: "Welcome, Super Admin",
      });

      navigate("/admin/dashboard");

    } catch (error) {
      console.error("Login error:", error);

      toast({
        title: "Login Failed",
        description:
          error?.detail ||
          error?.message ||
          "Invalid email or password",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center px-4">
      <Card className="w-full max-w-md p-8 bg-white/10 backdrop-blur-lg border-white/20">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Super Admin Portal
          </h1>
          <p className="text-gray-300">
            Manage recruiters, companies, and platform
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Label htmlFor="email" className="text-gray-200">
              Email Address
            </Label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@naukri.com"
              className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </div>

          <div>
            <Label htmlFor="password" className="text-gray-200">
              Password
            </Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className="mt-1 bg-white/10 border-white/20 text-white placeholder:text-gray-400"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold h-12"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Authenticating...
              </>
            ) : (
              "Login as Super Admin"
            )}
          </Button>
        </form>

        <div className="mt-6 text-center space-y-2">
          <Link to="/recruiter/login" className="text-purple-300 hover:text-purple-200 text-sm block">
            Recruiter Login
          </Link>
          <Link to="/login" className="text-gray-400 hover:text-gray-300 text-sm block">
            Job Seeker Login
          </Link>
        </div>
      </Card>
    </div>
  );
};

export default AdminLogin;